import { useEffect, useRef, type ReactNode } from 'react';
import Image from 'next/image';
import type { RichTextField, RTImageNode, RTNode } from '@prismicio/client';
import { PrismicRichText, type JSXMapSerializer } from '@prismicio/react';
import cn from 'classnames';

import {
  ATTRIBUTION_LABEL,
  hasLabel,
  PULL_QUOTE_LABEL,
  richContentBlockHeading,
} from '@adapters/prismic/transforms/richContentBlock';
import useObjectVisibility from '@hooks/useObjectVisibility';
import { trackRichContentBlockView } from '_utils/GA4/trackRichContentBlockEvent';
import RichText from '_components/RichText';
import { richTextHyperlink } from '_components/RichText/hyperlink';
import { imageLinkAttributes } from '_components/RichText/linkAttributes';
import QuotationMark from './quotation-mark.svg';
import type { RichContentBlockColumn, RichContentBlockProps } from './types';

import styles from './RichContentBlock.module.scss';

export type {
  RichContentBlockColumn,
  RichContentBlockColumnKind,
  RichContentBlockProps,
  RichContentBlockTracking,
} from './types';

// Share of the block that must be on screen to count as a view. Kept low
// because a stacked two-column block can be several screens tall on mobile,
// and the observer can never report more of it than fits the viewport.
const VIEW_THRESHOLD = 0.1;

// The widest each image renders on desktop: the image composition's 678px
// image, and the 710px one-column measure for images inside copy. Below
// tablet-xl (988px) everything is one full-width column.
const IMAGE_SIZES = '(min-width: 988px) 678px, 100vw';
const INLINE_IMAGE_SIZES = '(min-width: 988px) 710px, 100vw';

// The quote is display type, not body copy, so its text gets its own class
// rather than `RichText`'s. Authors can put the Pull quote label on any text
// block, so a labelled heading or list item renders as quote text too,
// rather than being silently left out. The label itself carries no markup.
const quoteText = ({ children }: { children: ReactNode }) => (
  <p className={styles.quoteText}>{children}</p>
);

const quoteComponents: JSXMapSerializer = {
  paragraph: quoteText,
  heading1: quoteText,
  heading2: quoteText,
  heading3: quoteText,
  heading4: quoteText,
  heading5: quoteText,
  heading6: quoteText,
  preformatted: quoteText,
  list: ({ children }) => <>{children}</>,
  oList: ({ children }) => <>{children}</>,
  listItem: quoteText,
  oListItem: quoteText,
  hyperlink: richTextHyperlink,
  label: ({ children }) => <>{children}</>,
};

// The attribution is rich text too, so a source link survives. Each block
// runs inline in the `<cite>`; the leading space separates it from the one
// before and collapses at the start of the line.
const citeLine = ({ children }: { children: ReactNode }) => (
  <span> {children}</span>
);

const citeComponents: JSXMapSerializer = {
  paragraph: citeLine,
  heading1: citeLine,
  heading2: citeLine,
  heading3: citeLine,
  heading4: citeLine,
  heading5: citeLine,
  heading6: citeLine,
  preformatted: citeLine,
  list: ({ children }) => <>{children}</>,
  oList: ({ children }) => <>{children}</>,
  listItem: citeLine,
  oListItem: citeLine,
  hyperlink: richTextHyperlink,
  label: ({ children }) => <>{children}</>,
};

// `key` is the segment's position, fixed once the column is split.
type Segment =
  | { type: 'richText'; key: number; nodes: RTNode[] }
  | { type: 'quote'; key: number; nodes: RTNode[]; attribution: RTNode[] };

/**
 * Splits a column into runs of ordinary rich text and pull quotes. A quote
 * is one or more paragraphs labelled Pull quote, plus any Quote attribution
 * paragraphs straight after it. An attribution with no quote above it is
 * just a paragraph.
 */
const segment = (content: RichTextField): Segment[] =>
  content.reduce<Segment[]>((segments, node) => {
    const last = segments[segments.length - 1];

    if (hasLabel(node, PULL_QUOTE_LABEL)) {
      if (last?.type === 'quote' && !last.attribution.length) {
        last.nodes.push(node);
      } else {
        segments.push({
          type: 'quote',
          key: segments.length,
          nodes: [node],
          attribution: [],
        });
      }
    } else if (last?.type === 'quote' && hasLabel(node, ATTRIBUTION_LABEL)) {
      last.attribution.push(node);
    } else if (last?.type === 'richText') {
      last.nodes.push(node);
    } else {
      segments.push({ type: 'richText', key: segments.length, nodes: [node] });
    }

    return segments;
  }, []);

const isBlankText = (node: RTNode) => !('text' in node && node.text.trim());

const Quote = ({
  nodes,
  attribution,
}: {
  nodes: RTNode[];
  attribution: RTNode[];
}) => {
  const cite = attribution.filter((node) => !isBlankText(node));

  return (
    <figure className={styles.quote}>
      <div className={styles.quoteRow}>
        <QuotationMark
          className={styles.quotationMark}
          aria-hidden="true"
          focusable="false"
        />
        <blockquote className={styles.blockquote}>
          <PrismicRichText
            field={nodes as RichTextField}
            components={quoteComponents}
          />
        </blockquote>
      </div>
      {cite.length > 0 && (
        <figcaption className={styles.attribution}>
          <cite className={styles.cite}>
            <PrismicRichText
              field={cite as RichTextField}
              components={citeComponents}
            />
          </cite>
        </figcaption>
      )}
    </figure>
  );
};

const ColumnContent = ({
  content,
  className,
}: {
  content: RichTextField;
  className?: string;
}) => (
  <div className={cn(styles.column, className)}>
    {segment(content).map((part) =>
      part.type === 'quote' ? (
        <Quote
          key={part.key}
          nodes={part.nodes}
          attribution={part.attribution}
        />
      ) : (
        <RichText
          key={part.key}
          field={part.nodes as RichTextField}
          imageSizes={INLINE_IMAGE_SIZES}
        />
      )
    )}
  </div>
);

// An `image` column holds only images; the composition shows the first.
const Media = ({
  content,
  priority,
}: {
  content: RichTextField;
  priority?: boolean;
}) => {
  const image = (content as RTNode[]).find(
    (node): node is RTImageNode => node.type === 'image'
  );
  if (!image?.url) return null;

  const img = (
    <Image
      className={styles.image}
      src={image.url}
      // Prismic stores a missing alt as null; empty marks it decorative.
      alt={image.alt ?? ''}
      fill
      sizes={IMAGE_SIZES}
      priority={priority}
    />
  );
  // The image's own Link field, set in the image card in Prismic.
  const link = imageLinkAttributes(image);

  return link ? (
    <a className={cn(styles.media, styles.mediaLink)} {...link}>
      {img}
    </a>
  ) : (
    <div className={styles.media}>{img}</div>
  );
};

const TwoColumns = ({
  columns,
  priority,
}: {
  columns: [RichContentBlockColumn, RichContentBlockColumn];
  priority?: boolean;
}) => {
  const [left, right] = columns;

  // With an image in both columns, the left one is the composition image.
  if (left.kind === 'image' || right.kind === 'image') {
    const imageCopy = (column: RichContentBlockColumn) => (
      <ColumnContent content={column.content} className={styles.imageCopy} />
    );

    return (
      <div className={cn(styles.inner, styles.innerImage)}>
        {left.kind === 'image' ? (
          <Media content={left.content} priority={priority} />
        ) : (
          imageCopy(left)
        )}
        {left.kind !== 'image' ? (
          <Media content={right.content} priority={priority} />
        ) : (
          imageCopy(right)
        )}
      </div>
    );
  }

  // The lead column (a heading or a pull quote) sets the composition; with
  // neither, the left column leads. The body gets Figma's wider measure
  // when the lead sits on the right.
  const leads = (column: RichContentBlockColumn) =>
    column.kind === 'heading' || column.kind === 'quote';
  const leadIsRight = !leads(left) && leads(right);

  const leadClass = (column: RichContentBlockColumn) =>
    column.kind === 'quote' ? styles.quoteColumn : styles.headingColumn;
  const bodyClass = cn(styles.bodyColumn, {
    [styles.bodyColumnWide]: leadIsRight,
  });

  const leftColumn = (
    <ColumnContent
      content={left.content}
      className={leadIsRight ? bodyClass : leadClass(left)}
    />
  );

  // A heading always stacks above its copy on mobile (ED-249 QA), so a
  // heading on the right goes first in the markup; `headingRight` puts it
  // back on the right on desktop. A quote on the right keeps the left
  // column's order.
  if (leadIsRight && right.kind === 'heading') {
    return (
      <div className={styles.inner}>
        <ColumnContent
          content={right.content}
          className={cn(leadClass(right), styles.headingRight)}
        />
        {leftColumn}
      </div>
    );
  }

  return (
    <div className={styles.inner}>
      {leftColumn}
      <ColumnContent
        content={right.content}
        className={leadIsRight ? leadClass(right) : bodyClass}
      />
    </div>
  );
};

/**
 * Rich Content Block (ED-249, Figma 886:34913): free-form CMS rich text in
 * one or two columns. Authors don't pick a layout. Each column's content
 * decides its treatment (an image, a pull quote, a heading or body copy),
 * and the column order is the visual, reading and mobile-stack order.
 */
const RichContentBlock = ({
  columns,
  priority,
  tracking,
}: RichContentBlockProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  // Untracked blocks never attach the ref, so the hook observes nothing.
  const isVisible = useObjectVisibility(rootRef, VIEW_THRESHOLD);
  const hasTrackedView = useRef(false);

  // The handlers pass a fresh `tracking` object each render; depend on its
  // values so the effect only re-runs when something meaningful changes.
  const interactionLocation = tracking?.interactionLocation;
  const trackingIndex = tracking?.index;

  // ED-592: once per block per page load, however often it scrolls back in.
  useEffect(() => {
    if (
      !interactionLocation ||
      trackingIndex === undefined ||
      !isVisible ||
      hasTrackedView.current
    ) {
      return;
    }
    hasTrackedView.current = true;
    trackRichContentBlockView({
      interactionLocation,
      eventPage: window.location.pathname,
      heading: richContentBlockHeading(columns),
      index: trackingIndex,
    });
  }, [columns, interactionLocation, isVisible, trackingIndex]);

  return (
    <div className={styles.root} ref={tracking ? rootRef : undefined}>
      {columns.length === 2 ? (
        <TwoColumns columns={columns} priority={priority} />
      ) : (
        <div className={cn(styles.inner, styles.innerSingle)}>
          {columns[0].kind === 'image' ? (
            <Media content={columns[0].content} priority={priority} />
          ) : (
            <ColumnContent
              content={columns[0].content}
              className={styles.singleColumn}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default RichContentBlock;
